import React from "react";
import { Helmet } from "react-helmet";
import { Link, withRouter } from "react-router-dom";
import { useQuery } from "react-apollo-hooks";
import ReactMoment from "react-moment";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import {
  MANAGER_DONATION_DETAIL_QUERY,
  MANAGER_DONATION_LEDGER_LIST_QUERY,
} from "../../Queries";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../../../Components/Panel/Panel";
import { graphqlUri } from "../../../../Config/Env";

export default withRouter(({ match, location }) => {
  const donationId = match.params.donationId;

  const { data: dData } = useQuery(MANAGER_DONATION_DETAIL_QUERY, {
    variables: { id: donationId },
  });
  const donation = dData && dData.ManagerDonationDetail;
  const donationTitle = donation ? donation.title : "";

  const onDownloadLedgers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${graphqlUri}/excels/donationLedgers?donationId=${donationId}`,
        {
          method: "POST",
          mode: "cors",
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        }
      );
      if (!res.ok) {
        const msg = await res.text();
        toast.error(msg || "엑셀 다운로드에 실패했습니다.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `기부내역_${donationTitle || donationId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      a.remove();
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  const { data, loading } = useQuery(MANAGER_DONATION_LEDGER_LIST_QUERY, {
    variables: { donationId, skip: 0, first: 1000 },
    fetchPolicy: "network-only",
  });
  const result = data && data.ManagerDonationLedgerList;
  const ledgers = result ? result.ledgers : [];
  const count = result ? result.count : 0;

  return (
    <>
      <Helmet>
        <title>기부 내역 | 한밭페이</title>
      </Helmet>
      <div>
        <ol className="breadcrumb float-xl-right">
          <li className="breadcrumb-item">
            <Link to={`/manager/donations${location.search}`}>담당 기부</Link>
          </li>
          <li className="breadcrumb-item active">기부 내역</li>
        </ol>
        <h1 className="page-header">{donationTitle} 기부 내역</h1>

        <div className="m-b-15">
          <Link
            to={`/manager/donations${location.search}`}
            className="btn btn-white"
          >
            <i className="fa fa-list m-r-5"></i>목록
          </Link>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            background: "#fff",
            borderRadius: 6,
            overflow: "hidden",
            boxShadow: "0 1px 1px rgba(0,0,0,.1)",
            marginBottom: 15,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 26,
              padding: "20px 28px",
              minWidth: 320,
            }}
          >
            {[
              { label: "드림", value: donation ? donation.totalDRM : 0 },
              { op: "+" },
              { label: "드림 포인트", value: donation ? donation.totalDNC : 0 },
              { op: "=" },
              {
                label: "총 금액",
                value: donation
                  ? (donation.totalDRM || 0) + (donation.totalDNC || 0)
                  : 0,
              },
            ].map((item, i) =>
              item.op ? (
                <div key={i} style={{ fontSize: 26, color: "#adb5bd" }}>
                  {item.op}
                </div>
              ) : (
                <div key={i}>
                  <div style={{ fontSize: 13, color: "#8a94a6" }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 600, marginTop: 2 }}>
                    <NumberFormat
                      value={item.value}
                      displayType={"text"}
                      thousandSeparator={true}
                      suffix={"원"}
                    />
                  </div>
                </div>
              )
            )}
          </div>
          <div
            style={{
              background: "#212529",
              color: "#fff",
              padding: "20px 32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "right",
              minWidth: 220,
            }}
          >
            <div style={{ fontSize: 13, color: "#adb5bd" }}>목표 금액</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 2 }}>
              <NumberFormat
                value={donation ? donation.goalAmount : 0}
                displayType={"text"}
                thousandSeparator={true}
                suffix={"원"}
              />
            </div>
          </div>
        </div>

        <Panel>
          <PanelHeader noButton={true}>
            기부 내역 ({loading ? "..." : count}건)
            <button
              className="btn btn-white btn-xs pull-right"
              onClick={onDownloadLedgers}
            >
              <i className="fa fa-download m-r-5"></i>내역 다운로드
            </button>
          </PanelHeader>
          <PanelBody className="p-0">
            <div className="table-responsive">
              <table className="table table-bordered table-striped m-b-0">
                <thead>
                  <tr>
                    <th>일자</th>
                    <th>내용</th>
                    <th>통화</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {(loading || ledgers.length === 0) && (
                    <tr>
                      <td colSpan={4} className="text-center">
                        {loading ? "불러오는 중..." : "기부 내역이 없습니다."}
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    ledgers.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <ReactMoment format="YYYY-MM-DD HH:mm:ss">
                            {l.createdAt}
                          </ReactMoment>
                        </td>
                        <td>{l.title}</td>
                        <td>{l.currency}</td>
                        <td align="right">
                          <NumberFormat
                            value={l.value}
                            displayType={"text"}
                            thousandSeparator={true}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </PanelBody>
        </Panel>
      </div>
    </>
  );
});
